import styled from 'styled-components'
import { ReactNode, useEffect } from 'react'
import { connect, ErrorMessage, Field, getIn } from 'formik'

import { SVGIcon } from 'shared/components/Icons'
import { StateDropDownSelect } from 'checkout/components/StateDropdownSelect'
import { colors, fonts } from 'shared/lib'
import { useGetStatesQuery } from 'api/countriesApi'

type StateDropdownProps = {
  countryISOCode: string
  disabled?: boolean
  formik?
  name: string
  qaAttr?: string
  title: string | ReactNode
  useStateCodeAsValue?: boolean
}

const StateDropdownField = ({
  countryISOCode,
  disabled,
  formik,
  name,
  qaAttr,
  title,
  useStateCodeAsValue = false,
}: StateDropdownProps) => {
  const hasError = getIn(formik.errors, name) && getIn(formik.touched, name)
  const { data, isLoading, isError } = useGetStatesQuery(countryISOCode || 'US')
  const states = data?.states || []

  const handleChangeAndBlur = (e: React.FormEvent<HTMLInputElement>) => {
    const value = e.currentTarget.value
    formik.setFieldValue(name, value)
  }

  useEffect(() => {
    if (useStateCodeAsValue && states) {
      const stateName = states.find((state) => state?.stateCode === formik?.values?.state)?.name
      if (stateName) {
        formik.setFieldValue('state', stateName)
      }
    }
  }, [useStateCodeAsValue, states, formik?.values?.state])

  if (isError || isLoading) {
    return null
  }

  return (
    <FieldBox>
      <FormLabel>
        {title}
        <StyledFormikErrorMessage component="span" data-qa={`${qaAttr}ErrorInLine`} name={name} />
      </FormLabel>
      <SelectBox>
        <Field
          autoComplete="address-line1"
          component={StateDropDownSelect}
          disabled={disabled || !states || !states.length}
          error={hasError}
          name={name}
          onBlur={handleChangeAndBlur}
          onChange={handleChangeAndBlur}
          qaAttr={qaAttr}
          states={states}
        />
        <StyledCaret color={disabled ? colors.FC2_GREY : colors.FC2_BLACK} name="caret" />
      </SelectBox>
    </FieldBox>
  )
}

const FieldBox = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 15px;
`
const FormLabel = styled.label`
  ${fonts.SUBTITLE_3}
  color: ${colors.FC2_GREY};
  display: flex;
  justify-content: space-between;
`
const SelectBox = styled.div`
  position: relative;
`
const StyledCaret = styled(SVGIcon).attrs({
  name: 'caret',
})`
  position: absolute;
  right: 15px;
  top: 40%;
  transform: translateY(-50%);
  width: 14px;
`
const StyledFormikErrorMessage = styled(ErrorMessage)`
  color: ${colors.FC2_RED};
`

export default connect(StateDropdownField)
